import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Button, Space, Input, InputNumber, Form, Select, Row, Col, Tag, Typography, notification,
  DatePicker, Modal, Popconfirm, Upload, Tooltip, Drawer, AutoComplete, Table
} from 'antd';
import {
  PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined,
  ExportOutlined, ImportOutlined, AppleOutlined, TeamOutlined,
  ClearOutlined, FileExcelOutlined, DownloadOutlined, UploadOutlined,
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import UploadButton from '../../../components/UploadButton';
import { TableCustom } from '../../../components/TableCustom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  deletePremiumOrderRequest,
  getListPremiumOrdersRequest,
  getPremiumOrderOptionsRequest,
  createPremiumOrderRequest,
  updatePremiumOrderRequest,
  bulkAssignStaffRequest,
  exportPremiumOrdersRequest,
  importPremiumOrdersRequest,
  checkAppleOrderIdRequest,
} from '../../../services/cms/premiumOrderServiceRequest';
import { listAllActiveServicesRequest } from '../../../services/cms/serviceServiceRequest';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
const { Text } = Typography;
const { TextArea } = Input;

// Full status color mapping
const STATUS_COLORS = {
  1: 'blue',
  2: 'orange',
  3: 'red',
  4: 'cyan',
  5: 'green',
  6: 'purple',
  7: 'default',
  8: 'geekblue',
  9: 'lime',
  10: 'gold',
  11: 'volcano',
  12: 'magenta',
  13: 'processing',
};



const QuanLyDonHang = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [formSearch] = Form.useForm();

  // Đọc query params từ URL để hỗ trợ deep-linking (VD: từ MB Push BDSD link sang)
  const initialParams = useMemo(() => {
    try {
      const saved = sessionStorage.getItem('cms_quanlydonhang_params');
      if (saved) return JSON.parse(saved);
    } catch (e) {}
    const init = { page: 1, limit: 20 };
    const titleParam = searchParams.get('title');
    if (titleParam) init.title = titleParam;
    return init;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [params, setParams] = useState(initialParams);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  useEffect(() => {
    if (params) {
      sessionStorage.setItem('cms_quanlydonhang_params', JSON.stringify(params));
    }
  }, [params]);

  // Set giá trị ban đầu vào form search nếu có query param hoặc từ sessionStorage
  useEffect(() => {
    const titleParam = searchParams.get('title');
    if (titleParam) {
      formSearch.setFieldsValue({ user_id_search: titleParam });
    } else {
      try {
        const saved = sessionStorage.getItem('cms_quanlydonhang_params');
        if (saved) {
          const parsed = JSON.parse(saved);
          const formValues = { ...parsed };
          if (parsed.title) formValues.user_id_search = parsed.title;
          if (parsed.date_from) formValues.date_from = dayjs(parsed.date_from);
          if (parsed.date_to) formValues.date_to = dayjs(parsed.date_to);
          if (parsed.payment_date_from) formValues.payment_date_from = dayjs(parsed.payment_date_from);
          if (parsed.payment_date_to) formValues.payment_date_to = dayjs(parsed.payment_date_to);
          if (parsed.modify_date_from) formValues.modify_date_from = dayjs(parsed.modify_date_from);
          if (parsed.modify_date_to) formValues.modify_date_to = dayjs(parsed.modify_date_to);
          formSearch.setFieldsValue(formValues);
        }
      } catch (e) {}
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [assignStaffId, setAssignStaffId] = useState(null);

  const [appleModalVisible, setAppleModalVisible] = useState(false);
  const [appleOrderId, setAppleOrderId] = useState('');
  const [appleResult, setAppleResult] = useState(null);

  const [importModalVisible, setImportModalVisible] = useState(false);
  const [importPreviewData, setImportPreviewData] = useState([]);
  const [callDrawerVisible, setCallDrawerVisible] = useState(false);
  const [callDrawerData, setCallDrawerData] = useState([]);

  const dataRedux = useSelector((state) => state.user);
  const userRole = dataRedux?.info?.role;
  const isAdmin = userRole === 9999;
  const userPermissions = (dataRedux?.info?.permissions || []).map((item) => item.key_menu);
  const hasAssignPermission = isAdmin || userPermissions.includes('/quan-ly-don-hang');

  const { data: dataOrders, refetch, isLoading: isLoadingOrders } = useQuery({
    queryKey: ['dataPremiumOrders', params],
    queryFn: () => getListPremiumOrdersRequest(params),
  });

  const { data: options } = useQuery({
    queryKey: ['premiumOrderOptions'],
    queryFn: () => getPremiumOrderOptionsRequest({}),
  });

  const { data: allActiveServices } = useQuery({
    queryKey: ['allActiveServicesForOrder'],
    queryFn: () => listAllActiveServicesRequest({}),
  });

  const activeServiceOptions = useMemo(() => {
    if (!allActiveServices) return [];
    return allActiveServices.map(s => ({ value: s.id, label: `${s.id} - ${s.name}` }));
  }, [allActiveServices]);

  const { mutate: mutateDelete } = useMutation({
    mutationFn: deletePremiumOrderRequest,
    onSuccess: (data) => {
      if (data?.status === 1) {
        notification.success({ message: 'Xóa đơn hàng thành công!' });
        refetch();
      } else notification.error({ message: data?.message || 'Có lỗi xảy ra!' });
    },
  });

  const { mutate: mutateBulkAssign, isPending: isBulkAssigning } = useMutation({
    mutationFn: bulkAssignStaffRequest,
    onSuccess: (data) => {
      if (data?.status === 1) {
        notification.success({ message: `Phân ${selectedRowKeys.length} đơn thành công!` });
        setSelectedRowKeys([]); setAssignStaffId(null); refetch();
      } else notification.error({ message: data?.message || 'Có lỗi xảy ra!' });
    },
  });

  const { mutate: mutateImport } = useMutation({
    mutationFn: importPremiumOrdersRequest,
    onSuccess: (data) => {
      if (data?.status === 1) {
        notification.success({ message: `Import thành công ${data?.data?.imported || 0} đơn!` });
        setImportModalVisible(false); setImportPreviewData([]); refetch();
      } else notification.error({ message: data?.message || 'Có lỗi xảy ra!' });
    },
  });

  const statusOptions = useMemo(() => {
    if (!options?.statuses) return [];
    return Object.entries(options.statuses).map(([k, v]) => ({ value: Number(k), label: v }));
  }, [options?.statuses]);

  const reasonOptions = useMemo(() => {
    if (!options?.reasons) return [];
    if (Array.isArray(options.reasons)) return options.reasons.map((r) => ({ value: r, label: r }));
    return Object.entries(options.reasons).map(([k, v]) => ({ value: v, label: v }));
  }, [options?.reasons]);


  const handleSubmitForm = (values) => {
    const temp = { page: 1, limit: params.limit };
    // Lấy user_id_search từ form thay vì title
    if (values?.user_id_search) temp.title = values.user_id_search;
    if (values?.status !== undefined && values?.status !== -1) temp.status = values.status;
    if (values?.staff_id) temp.staff_id = values.staff_id;
    if (values?.modify_by) temp.modify_by = values.modify_by;
    if (values?.campaign_id) temp.campaign_id = values.campaign_id;
    if (values?.premium_type_id) temp.premium_type_id = values.premium_type_id;
    if (values?.features_id) temp.features_id = values.features_id;
    if (values?.work_id) temp.work_id = values.work_id;
    if (values?.order_type) temp.order_type = values.order_type;
    if (values?.payment_channel) temp.payment_channel = values.payment_channel;
    if (values?.reason) temp.reason = values.reason;
    if (values?.content) temp.content = values.content;
    if (values?.platform) temp.platform = values.platform;
    if (values?.app_version) temp.app_version = values.app_version;
    if (values?.call_numb) temp.call_numb = values.call_numb;
    if (values?.date_from) temp.date_from = values.date_from.format('YYYY-MM-DD');
    if (values?.date_to) temp.date_to = values.date_to.format('YYYY-MM-DD');
    if (values?.payment_date_from) temp.payment_date_from = values.payment_date_from.format('YYYY-MM-DD');
    if (values?.payment_date_to) temp.payment_date_to = values.payment_date_to.format('YYYY-MM-DD');
    if (values?.modify_date_from) temp.modify_date_from = values.modify_date_from.format('YYYY-MM-DD');
    if (values?.modify_date_to) temp.modify_date_to = values.modify_date_to.format('YYYY-MM-DD');
    setParams(temp);
  };

  const openCreateModal = () => {
    navigate('/cms/quan-ly-don-hang/new');
  };

  const openEditModal = (record) => {
    navigate(`/cms/quan-ly-don-hang/${record.id}`);
  };

  const handleExport = useCallback(async () => {
    try {
      notification.info({ message: 'Đang xuất dữ liệu...' });
      const result = await exportPremiumOrdersRequest(params);
      if (result?.header && result?.rows) {
        const csvContent = [result.header, ...result.rows]
          .map(row => row.map(cell => `"${String(cell || '').replace(/"/g, '""')}"`).join(','))
          .join('\n');
        const BOM = '\uFEFF';
        const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `don_hang_premium_${new Date().toISOString().slice(0, 10)}.csv`;
        link.click();
        URL.revokeObjectURL(url);
        notification.success({ message: `Xuất ${result.total} đơn hàng thành công!` });
      }
    } catch (error) {
      notification.error({ message: 'Lỗi khi xuất file!' });
    }
  }, [params]);

  const handleImportFile = useCallback((file) => {
    if (file.size / 1048576 > 250) {
      notification.error({ message: 'Không thể upload file có dung lượng > 250MB' });
      return false;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const firstSheetName = workbook.SheetNames?.[0];
        const sheet = workbook.Sheets?.Sheet1 || (firstSheetName ? workbook.Sheets[firstSheetName] : null);
        if (!sheet) {
          notification.error({ message: 'Không tìm thấy sheet dữ liệu trong file. Vui lòng kiểm tra lại.' });
          return;
        }
        // Parse theo cấu trúc cột giống lichviet_cms: A=customer_id, B=user_id, C=fullname, D=phone, E=features_id, F=premium_type_id, G=staff_id, H=campaign_id
        const keys = Object.keys(sheet);
        const dHeaders = keys.filter((key) => key.startsWith('D') && /^D\d+$/.test(key));
        const items = [];
        for (let i = 0; i < dHeaders.length; i++) {
          const rowIdx = parseInt(dHeaders[i].replace('D', ''));
          if (rowIdx <= 1) continue; // skip header row
          const item = {
            customer_id: sheet['A' + rowIdx]?.v ?? '',
            user_id: sheet['B' + rowIdx]?.v ?? '',
            fullname: sheet['C' + rowIdx]?.v ?? '',
            phone: sheet['D' + rowIdx]?.v ?? '',
            features_id: sheet['E' + rowIdx]?.v ?? '',
            premium_type_id: sheet['F' + rowIdx]?.v ?? '',
            staff_id: sheet['G' + rowIdx]?.v ?? '',
            campaign_id: sheet['H' + rowIdx]?.v ?? '',
          };
          if (item.phone || item.user_id) items.push(item);
        }
        if (items.length === 0) {
          notification.error({ message: 'Không tìm thấy dữ liệu hợp lệ trong file!' });
          return;
        }
        setImportPreviewData(items);
        notification.success({ message: `Đã đọc ${items.length} dòng dữ liệu từ file.` });
      } catch (err) {
        console.error('Import parse error:', err);
        notification.error({ message: 'Lỗi đọc file! Vui lòng kiểm tra định dạng file Excel.' });
      }
    };
    reader.onerror = () => {
      notification.error({ message: 'Lỗi đọc file!' });
    };
    reader.readAsBinaryString(file);
    return false;
  }, []);

  const handleConfirmImport = useCallback(() => {
    if (importPreviewData.length === 0) {
      notification.warning({ message: 'Chưa có dữ liệu để upload!' });
      return;
    }
    mutateImport({ items: importPreviewData });
  }, [importPreviewData, mutateImport]);

  const handleCheckApple = useCallback(async () => {
    if (!appleOrderId) return;
    try {
      const result = await checkAppleOrderIdRequest({ order_id: appleOrderId });
      setAppleResult(result);
    } catch { notification.error({ message: 'Không thể kiểm tra Apple Order ID!' }); }
  }, [appleOrderId]);

  const handleBulkAssign = () => {
    if (!assignStaffId) { notification.warning({ message: 'Vui lòng chọn nhân viên!' }); return; }
    if (!selectedRowKeys.length) { notification.warning({ message: 'Vui lòng chọn đơn hàng!' }); return; }
    mutateBulkAssign({ ids: selectedRowKeys, staff_id: assignStaffId });
  };


  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 70, fixed: 'left' },
    { title: 'Ngày gửi', dataIndex: 'create_time_formatted', key: 'create_time', width: 130 },
    {
      title: 'UserID', dataIndex: 'user_id', key: 'user_id', width: 80,
      render: (val) => val ? (
        <a href={`/cms/quan-ly-user/${val}`} target="_blank" rel="noopener noreferrer">{val}</a>
      ) : '--',
    },
    { title: 'SĐT', dataIndex: 'phone', key: 'phone', width: 110 },
    {
      title: 'Tính năng (Dịch vụ)', key: 'features_name', width: 150, ellipsis: true,
      render: (_, r) => {
        const service = allActiveServices?.find(s => String(s.id) === String(r.features_id));
        return service ? `${service.id} - ${service.name}` : (r.features_name || '--');
      },
    },
    {
      title: 'Tên gói', dataIndex: 'premium_type_name', key: 'premium_type_name', width: 150,
      ellipsis: { showTitle: true },
    },
    {
      title: 'Nội dung', key: 'content_col', width: 160, ellipsis: { showTitle: true },
      render: (_, r) => {
        const parts = [r.work_name, r.content, r.lv_camp].filter(Boolean);
        return parts.join(' / ') || '--';
      },
    },
    { title: 'Chiến dịch', dataIndex: 'campaign_name', key: 'campaign_name', width: 120, ellipsis: true },
    {
      title: 'Ghi chú', key: 'last_note', width: 200, ellipsis: { showTitle: false },
      render: (_, record) => (
        <Tooltip title={<span style={{ whiteSpace: 'pre-wrap' }}>{record.last_note}</span>}>
          <Text
            style={{ fontSize: 12, cursor: record.order_calls?.length ? 'pointer' : 'default', whiteSpace: 'pre-line' }}
            onClick={() => {
              if (record.order_calls?.length) { setCallDrawerData(record.order_calls); setCallDrawerVisible(true); }
            }}
          >
            {record.last_note || '--'}
            {record.order_calls?.length > 1 && <Tag style={{ marginLeft: 4 }} color="blue">{record.order_calls.length} lần</Tag>}
          </Text>
        </Tooltip>
      ),
    },
    { title: 'Nền tảng', dataIndex: 'platform', key: 'platform', width: 80 },
    { title: 'Voucher', dataIndex: 'voucher_id', key: 'voucher_id', width: 80, render: v => v || '--' },
    {
      title: 'NV Chăm sóc', key: 'staff_name', width: 120,
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          {r.staff_name && <Text style={{ fontSize: 12 }}><TeamOutlined /> {r.staff_name}</Text>}
          {r.modify_name && r.modify_name !== r.staff_name && <Text type="secondary" style={{ fontSize: 11 }}>CS: {r.modify_name}</Text>}
        </Space>
      ),
    },
    {
      title: 'Số tiền cần TT', dataIndex: 'pay_amount', key: 'pay_amount', width: 110,
      render: (val, record) => {
        // Chỉ hiển số tiền khi status là trạng thái thanh toán (giống CMS cũ)
        if (![5, 10, 11, 12, 13].includes(Number(record.status))) return '--';
        return val ? Number(val).toLocaleString('vi-VN') + 'đ' : '--';
      },
    },
    {
      title: 'Trạng thái', key: 'status_name', width: 120,
      render: (_, { status, status_name }) => <Tag color={STATUS_COLORS[status] || 'default'}>{status_name || '--'}</Tag>,
    },
    { title: 'Lý do', dataIndex: 'reason_name', key: 'reason_name', width: 100, ellipsis: true },
    {
      title: 'Chênh lệch', key: 'diff', width: 100,
      render: (_, r) => {
        const purchased = Number(r.price_purchased) || 0;
        const payAmount = Number(r.pay_amount) || 0;
        const diff = purchased - payAmount;
        if (diff === 0) return '0đ';
        return (diff > 0 ? '' : '-') + Math.abs(diff).toLocaleString('vi-VN') + 'đ';
      },
    },
    {
      title: 'Thiết lập', key: 'action', width: 100, fixed: 'right',
      render: (_, record) => (
        <Space size={0}>
          {record.status !== 7 && (
            <Button type="text" size="small" onClick={() => openEditModal(record)}><EditOutlined /></Button>
          )}
          <Popconfirm title="Xóa đơn hàng này?" onConfirm={() => mutateDelete({ id: record.id })} okText="Xóa" cancelText="Hủy">
            <Button type="text" size="small" danger><DeleteOutlined /></Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const rowSelection = hasAssignPermission ? {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
    preserveSelectedRowKeys: true,
    columnWidth: 48,
    getCheckboxProps: (record) => ({
      name: `row-${record.id}`,
    }),
  } : undefined;

  return (
    <div style={{ margin: 20 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 style={{ fontSize: 22, margin: 0 }}>Quản lý đơn hàng Premium</h1>
        <Space>
          <Button icon={<AppleOutlined />} onClick={() => setAppleModalVisible(true)}>Check Apple</Button>
          <Button icon={<ExportOutlined />} onClick={handleExport}>Xuất báo cáo</Button>
          <Button icon={<ImportOutlined />} onClick={() => setImportModalVisible(true)}>Upload danh sách</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>Thêm đơn hàng</Button>
        </Space>
      </div>

      {/* ===== Filters ===== */}
      <Form name="search_orders" form={formSearch} layout="vertical" requiredMark={false} onFinish={handleSubmitForm} style={{ marginBottom: 16 }}>
        <Row gutter={[12, 0]}>
          <Col xs={24} sm={12} md={6} xl={4}>
            <Form.Item name="user_id_search" label="Tìm kiếm">
              <Input placeholder="ID, SĐT, Email hoặc Tên" allowClear />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6} xl={4}>
            <Form.Item name="content" label="Nội dung">
              <Input placeholder="Nội dung" allowClear />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6} xl={4}>
            <Form.Item name="date_from" label="Ngày gửi từ">
              <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} placeholder="Từ ngày" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6} xl={4}>
            <Form.Item name="date_to" label="Ngày gửi đến">
              <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} placeholder="Đến ngày" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6} xl={4}>
            <Form.Item name="payment_date_from" label="Ngày TT từ">
              <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} placeholder="Từ ngày" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6} xl={4}>
            <Form.Item name="payment_date_to" label="Ngày TT đến">
              <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} placeholder="Đến ngày" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6} xl={4}>
            <Form.Item name="modify_date_from" label="Ngày sửa từ">
              <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} placeholder="Từ ngày" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6} xl={4}>
            <Form.Item name="modify_date_to" label="Ngày sửa đến">
              <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} placeholder="Đến ngày" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6} xl={4}>
            <Form.Item name="modify_by" label="NV Sửa">
              <Select showSearch allowClear placeholder="Chọn nhân viên sửa"
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                options={options?.staff?.map(s => ({ value: s.id, label: s.username })) || []}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[12, 0]}>
          <Col xs={24} sm={12} md={6} xl={4}>
            <Form.Item name="staff_id" label="NV Chăm sóc">
              <Select showSearch allowClear placeholder="Chọn nhân viên chăm sóc"
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                options={options?.staff?.map(s => ({ value: s.id, label: s.username })) || []}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6} xl={4}>
            <Form.Item name="call_numb" label="Lần gọi">
              <Select allowClear placeholder="Lần gọi" options={options?.callNumbOptions || []} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6} xl={4}>
            <Form.Item name="features_id" label="Tính năng (Dịch vụ)">
              <Select showSearch allowClear placeholder="Chọn dịch vụ"
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                options={activeServiceOptions}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6} xl={4}>
            <Form.Item name="premium_type_id" label="Gói mua">
              <Select showSearch allowClear placeholder="Chọn gói mua"
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                options={options?.premiumTypes?.map(pt => ({ value: pt.id, label: `${pt.id}-${pt.description || ''}-${pt.name}` })) || []}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6} xl={4}>
            {/* Issue #3 fix: platform options từ backend */}
            <Form.Item name="platform" label="Nền tảng">
              <Select allowClear placeholder="Chọn nền tảng"
                options={options?.platforms || []}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6} xl={4}>
            <Form.Item name="status" label="Trạng thái" initialValue={-1}>
              <Select options={[{ value: -1, label: 'Tất cả' }, ...statusOptions]} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[12, 0]}>
          <Col xs={24} sm={12} md={6} xl={4}>
            <Form.Item name="payment_channel" label="Kênh thanh toán">
              <Select showSearch allowClear placeholder="Chọn kênh thanh toán"
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                options={options?.paymentChannels || []}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6} xl={4}>
            <Form.Item name="campaign_id" label="Chiến dịch">
              <Select showSearch allowClear placeholder="Chọn chiến dịch"
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                options={options?.campaigns?.map(c => ({ value: c.id, label: c.title })) || []}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6} xl={4}>
            <Form.Item name="work_id" label="Chọn việc">
              <Select showSearch allowClear placeholder="Chọn việc"
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                options={options?.workList || []}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6} xl={4}>
            <Form.Item name="reason" label="Lý do">
              <Select allowClear placeholder="Chọn lý do" options={reasonOptions} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6} xl={4}>
            <Form.Item name="app_version" label="Phiên bản">
              <Select showSearch allowClear placeholder="Chọn phiên bản"
                options={options?.appVersions?.map(v => ({ value: v, label: v })) || []}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6} xl={4} style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 24 }}>
            <Space>
              <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>Tìm kiếm</Button>
              <Button icon={<ClearOutlined />} onClick={() => { formSearch.resetFields(); setParams({ page: 1, limit: params.limit }); }}>Xóa</Button>
            </Space>
          </Col>
        </Row>
      </Form>

      {/* ===== Chia order bar ===== */}
      {hasAssignPermission && (
        <div style={{ marginBottom: 12, padding: '8px 16px', background: '#f5f5f5', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 12 }}>
          <Text style={{ fontSize: 16 }}>Nhân viên:</Text>
          <Select
            showSearch allowClear placeholder="Chọn nhân viên" style={{ width: 250 }}
            value={assignStaffId} onChange={setAssignStaffId}
            filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            options={options?.staff?.map(s => ({ value: s.id, label: s.username })) || []}
          />
          <Button type="primary" style={{ background: '#5cb85c', borderColor: '#5cb85c' }} onClick={handleBulkAssign} loading={isBulkAssigning}>
            Duyệt Nhân viên
          </Button>
          {selectedRowKeys.length > 0 && (
            <>
              <Text type="secondary">({selectedRowKeys.length} đơn đã chọn)</Text>
              <Button size="small" danger onClick={() => setSelectedRowKeys([])}>Bỏ chọn</Button>
            </>
          )}
        </div>
      )}

      {/* Bulk delete */}
      <div style={{ marginBottom: 12 }}>
        {selectedRowKeys.length > 0 && (
          <Popconfirm title={`Xóa ${selectedRowKeys.length} đơn hàng?`} onConfirm={() => {
            selectedRowKeys.forEach(id => mutateDelete({ id }));
            setSelectedRowKeys([]);
          }}>
            <Button danger size="small" style={{ background: '#d43f3a', color: '#fff', border: 'none' }}>Xóa</Button>
          </Popconfirm>
        )}
      </div>

      {/* Table */}
      <TableCustom
        rowKey="id"
        columns={columns}
        dataSource={dataOrders?.data}
        totalItem={dataOrders?.total}
        params={params}
        setParams={setParams}
        loading={isLoadingOrders}
        rowSelection={rowSelection}
        scroll={{ x: 1980 }}
      />



      {/* Modal: Check Apple Order ID */}
      <Modal
        title="Kiểm tra Apple Order ID"
        open={appleModalVisible}
        onCancel={() => { setAppleModalVisible(false); setAppleResult(null); setAppleOrderId(''); }}
        footer={[
          <Button key="close" onClick={() => { setAppleModalVisible(false); setAppleResult(null); setAppleOrderId(''); }}>Đóng</Button>,
          <Button key="check" type="primary" icon={<AppleOutlined />} onClick={handleCheckApple}>Kiểm tra</Button>,
        ]}
      >
        <Input placeholder="Nhập Apple Order ID" value={appleOrderId} onChange={e => setAppleOrderId(e.target.value)} onPressEnter={handleCheckApple} style={{ marginBottom: 16 }} />
        {appleResult && (() => {
          const jwtPayload = appleResult?.data?.data?.data?.jwtPayload || appleResult?.data?.data?.jwtPayload || appleResult?.data?.jwtPayload;
          if (jwtPayload) {
            return (
              <div style={{ background: '#f5f5f5', padding: 12, borderRadius: 6, fontSize: 13, lineHeight: '24px' }}>
                <div><Text strong>Mã giao dịch:</Text> {jwtPayload.transactionId}</div>
                <div><Text strong>Mã giao dịch gốc:</Text> {jwtPayload.originalTransactionId}</div>
                <div><Text strong>Thời gian thanh toán:</Text> {jwtPayload.purchaseDate ? new Date(jwtPayload.purchaseDate).toLocaleString('vi-VN') : '--'}</div>
                <div><Text strong>Giá tiền:</Text> {jwtPayload.price ? `${parseFloat(jwtPayload.price) / 1000} ${jwtPayload.currency}` : '--'}</div>
                <div><Text strong>Loại giao dịch:</Text> {jwtPayload.type}</div>
                <div><Text strong>appAccountToken:</Text> {jwtPayload.appAccountToken}</div>
                <div><Text strong>Gói mua:</Text> {jwtPayload.productId}</div>
              </div>
            );
          }
          return (
            <div style={{ background: '#f5f5f5', padding: 12, borderRadius: 6, fontSize: 13, color: 'red' }}>
              <Text strong>Không tìm được thông tin orderID</Text>
            </div>
          );
        })()}
      </Modal>

      {/* Modal: Import — giống lichviet_cms */}
      <Modal
        title="Upload danh sách đơn hàng"
        open={importModalVisible}
        onCancel={() => { setImportModalVisible(false); setImportPreviewData([]); }}
        width={800}
        footer={[
          <Button key="close" onClick={() => { setImportModalVisible(false); setImportPreviewData([]); }}>Đóng</Button>,
          importPreviewData.length > 0 && (
            <Button key="upload" type="primary" icon={<UploadOutlined />} onClick={handleConfirmImport}>
              Upload {importPreviewData.length} đơn hàng
            </Button>
          ),
        ]}
      >
        <div style={{ marginBottom: 16 }}>
          <Space>
            <a href="/templates/template_premium_order.xlsx" target="_blank" rel="noopener noreferrer">
              <Button icon={<DownloadOutlined />} type="link" style={{ paddingLeft: 0 }}>Tải file mẫu excel</Button>
            </a>
          </Space>
        </div>
        <Upload.Dragger
          name="file"
          accept=".xlsx,.xls"
          showUploadList={false}
          beforeUpload={handleImportFile}
          style={{ marginBottom: 16 }}
        >
          <p className="ant-upload-drag-icon"><FileExcelOutlined style={{ fontSize: 48, color: '#52c41a' }} /></p>
          <p className="ant-upload-text">Kéo thả file Excel (.xlsx) vào đây hoặc click để chọn</p>
          <p className="ant-upload-hint">Cột: customer_id, user_id, fullname, phone, features_id, premium_type_id, staff_id, campaign_id</p>
        </Upload.Dragger>
        {importPreviewData.length > 0 && (
          <>
            <Text strong style={{ display: 'block', margin: '12px 0 8px' }}>Xem trước dữ liệu ({importPreviewData.length} dòng):</Text>
            <Table
              dataSource={importPreviewData.map((item, idx) => ({ ...item, key: idx }))}
              columns={[
                { title: 'STT', render: (_, __, idx) => idx + 1, width: 50 },
                { title: 'Customer ID', dataIndex: 'customer_id', width: 100 },
                { title: 'User ID', dataIndex: 'user_id', width: 80 },
                { title: 'Họ tên', dataIndex: 'fullname', width: 120, ellipsis: true },
                { title: 'SĐT', dataIndex: 'phone', width: 110 },
                { title: 'Tính năng', dataIndex: 'features_id', width: 80 },
                { title: 'Gói mua', dataIndex: 'premium_type_id', width: 80 },
                { title: 'NV', dataIndex: 'staff_id', width: 60 },
                { title: 'Chiến dịch', dataIndex: 'campaign_id', width: 80 },
              ]}
              size="small"
              pagination={{ pageSize: 5, size: 'small' }}
              scroll={{ x: 700 }}
            />
          </>
        )}
      </Modal>

      {/* Drawer: Call history */}
      <Drawer title="Lịch sử liên hệ" open={callDrawerVisible} onClose={() => { setCallDrawerVisible(false); setCallDrawerData([]); }} width={480}>
        {callDrawerData.length > 0 && (
          <div style={{ marginBottom: 12, padding: '8px 12px', background: '#f6f8fa', borderRadius: 8 }}>
            <Text strong>Tổng số lần gọi: {callDrawerData.length}</Text>
          </div>
        )}
        {callDrawerData.map((call, idx) => (
          <div key={idx} style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
            <Space direction="vertical" size={2}>
              <Text strong>Lần {call.call_numb || idx + 1}</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {call.modify_time ? new Date(call.modify_time).toLocaleString('vi-VN') : ''} — {call.modify_name || ''}
              </Text>
              {call.note && <Text>Nội dung: {call.note}</Text>}
              {call.reason && <Text style={{ color: '#fa8c16' }}>Lý do: {call.reason}</Text>}
              {call.status && <Tag color={STATUS_COLORS[call.status]}>{options?.statuses?.[call.status] || ''}</Tag>}
            </Space>
          </div>
        ))}
        {callDrawerData.length === 0 && <Text type="secondary">Chưa có lịch sử liên hệ</Text>}
      </Drawer>
    </div>
  );
};

export default QuanLyDonHang;
